import React, { useState } from 'react'

import { Icon } from '@mxenabled/mx-icons'
import { Text } from '@mxenabled/mxui'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'

interface FeedbackCardProps {
  closeAriaLabel: string
  feedbackTitle: string
  onClose: () => void
  onBackClick: () => void
  onSubmit: (selectedRadio: number, feedbackMessage?: string) => void
  radioOptions: string[]
  characterLimitText: string
  backButtonTitle: string
  submitButtonTitle: string
}

const CHARACTER_LIMIT = 250
const OTHER = 4

export const FeedbackCard = ({
  closeAriaLabel,
  feedbackTitle,
  onClose,
  onBackClick,
  onSubmit,
  radioOptions,
  characterLimitText,
  backButtonTitle,
  submitButtonTitle,
}: FeedbackCardProps) => {
  const theme = useTheme()
  const [selectedValue, setSelectedValue] = useState<number | null>(null)
  const [feedbackMessage, setFeedbackMessage] = useState('')

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.value, 10)
    setSelectedValue(index)
    if (index !== OTHER) {
      setFeedbackMessage('')
    }
  }

  const handleOtherTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackMessage(event.target.value)
  }

  const handleSubmit = () => {
    if (selectedValue !== null) {
      onSubmit(selectedValue, selectedValue === OTHER ? feedbackMessage : undefined)
    }
  }

  return (
    <Card sx={{ minWidth: 288, width: { sm: theme.breakpoints.values.xs }, borderRadius: '8px' }}>
      <Stack
        sx={{ p: 24, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Text variant="h3">{feedbackTitle}</Text>
        <IconButton
          aria-label={closeAriaLabel}
          onClick={onClose}
          sx={{ minHeight: 'unset', minWidth: 'unset', p: 0 }}
        >
          <Icon color="secondary" name="close" size={24} />
        </IconButton>
      </Stack>

      <Stack sx={{ px: 24 }}>
        <FormControl>
          <RadioGroup
            aria-labelledby="hide-insight-radio-buttons-group-label"
            name="radio-buttons-group"
            onChange={handleRadioChange}
            value={selectedValue}
          >
            {radioOptions.map((option, index) => (
              <FormControlLabel
                control={
                  <Radio
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 22,
                      },
                    }}
                  />
                }
                key={option}
                label={option}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: 15,
                    color: 'text.primary',
                  },
                }}
                value={index}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {selectedValue === OTHER && (
          <Stack sx={{ mt: 4, gap: 3 }}>
            <TextField
              maxRows={4}
              minRows={4}
              multiline={true}
              onChange={handleOtherTextChange}
              slotProps={{ htmlInput: { maxLength: CHARACTER_LIMIT } }}
              value={feedbackMessage}
            />

            <Text sx={{ fontWeight: 400, color: 'text.secondary' }} variant="subtitle2">
              {characterLimitText}
            </Text>
          </Stack>
        )}
      </Stack>

      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          p: 24,
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onBackClick} variant="text">
          {backButtonTitle}
        </Button>
        <Button
          disabled={selectedValue === null || (selectedValue === OTHER && !feedbackMessage.trim())}
          onClick={handleSubmit}
          variant="contained"
        >
          {submitButtonTitle}
        </Button>
      </Stack>
    </Card>
  )
}
